import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@salonomy/ui';
import { useAuth } from '../../lib/auth.js';
import { useToast } from '../../lib/toast.js';
import { placeLabel } from './locationColors.js';

/**
 * WS11 — the "create/edit routes to the CORRECT tenant" affordance for the unified diary. There is NO
 * cross-tenant write endpoint (RLS is the only boundary; ADR-0012). So booking or editing in another
 * place you work is NEVER an in-place cross-tenant write — it is a one-tap context switch into that
 * salon, after which the normal single-tenant diary handles the write under that tenant's context.
 *
 * This reuses the EXACTLY-approved StartPracticeModal.switchIn() pattern: auth.switchSalon(salonId)
 * (POST /v1/auth/switch — sets the principal + CLEARS the cache for the new tenant), then navigate('/')
 * into that salon's own diary. We never touch another tenant's data here; we only change which tenant
 * the session is in.
 */
export function SwitchToBookCard({
  salonId,
  salonName,
  isPrivatePractice,
  activeSalonId,
}: {
  salonId: string;
  salonName: string;
  isPrivatePractice: boolean;
  /** The salon the session is already in — no switch needed to book/edit there. */
  activeSalonId: string | undefined;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const { switchSalon } = useAuth();
  const [switching, setSwitching] = useState(false);

  const here = salonId === activeSalonId;
  const label = placeLabel(salonName, isPrivatePractice);

  async function go() {
    if (here) {
      // Already in this tenant — the diary write happens here; just land on it.
      void navigate({ to: '/' });
      return;
    }
    setSwitching(true);
    try {
      await switchSalon(salonId);
      void navigate({ to: '/' });
    } catch {
      toast.error(`Couldn’t switch into ${salonName} — try the salon switcher.`);
      setSwitching(false);
    }
  }

  return (
    <Button size="sm" variant="soft" onClick={go} disabled={switching}>
      {switching ? 'Switching…' : here ? 'Open this diary' : `Switch into ${label} to book`}
    </Button>
  );
}
